/**
 * FunnelLayout — centered card layout for the /start/* purchase funnel,
 * wrapped in the marketing site's header/footer chrome so the funnel looks
 * like a continuation of legacyorb.com.
 *
 * Renders the step progress bar (spec §3.5): Create Account · Review
 * Agreement · Product Selection · Shipping · Payment · App Access, plus a
 * "Welcome, {name} ({email}) · Sign out" strip when a Clerk session is
 * active. No DashboardLayout — funnel users are not admins.
 *
 * Design: docs/technical-design-website-product.md §4.1
 */
import { Check } from 'lucide-react';
import { useClerk, useUser } from '@clerk/clerk-react';
import { cn } from '@/lib/utils';
import { FUNNEL_STEP_LABELS } from '@/lib/funnel';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { MarketingChromeLayout } from '@/components/layout/MarketingChromeLayout';

interface FunnelLayoutProps {
  /** Current step, 1-based (1 = Create Account … 6 = App Access). */
  step: number;
  children: React.ReactNode;
  /** Max width of the content column. Defaults to the 3xl reading width. */
  width?: 'md' | '2xl' | '3xl';
}

export function FunnelLayout({ step, children, width = '3xl' }: FunnelLayoutProps) {
  // Customer-facing tab title — step label, never the SPA's "Admin Dashboard"
  // default (ClickUp 86baeu544).
  useDocumentTitle(`${FUNNEL_STEP_LABELS[step - 1] ?? 'Order'} · LegacyOrb`);

  const widthClass = width === 'md' ? 'max-w-md' : width === '2xl' ? 'max-w-2xl' : 'max-w-3xl';

  return (
    <MarketingChromeLayout>
      <div className="flex flex-col items-center px-4 py-8 sm:py-12">
        <div className={cn('flex w-full flex-col', widthClass)}>
          <SignedInBanner />

          <FunnelProgressBar step={step} />

          <div className="mt-6 sm:mt-8">{children}</div>
        </div>
      </div>
    </MarketingChromeLayout>
  );
}

/**
 * "Who am I" strip above the stepper, rendered only with an active Clerk
 * session. Buyers can land mid-funnel without realizing they are signed in
 * ("/" resumes the funnel for signed-in non-admins), and gift purchases on a
 * shared computer can be in the wrong account — naming the account with a
 * sign-out escape hatch removes both ambiguities.
 */
function SignedInBanner() {
  const { isLoaded, isSignedIn, user } = useUser();
  const { signOut } = useClerk();

  if (!isLoaded || !isSignedIn || !user) return null;

  const email = user.primaryEmailAddress?.emailAddress;

  const handleSignOut = async () => {
    await signOut();
    // Hard navigation — resets the zustand auth store and the react-query
    // cache (funnel state, orders) so a follow-up sign-in with a different
    // account can never see the previous account's data.
    window.location.href = '/sign-in';
  };

  return (
    <div className="mb-6 flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-4 py-2.5">
      <p className="min-w-0 truncate text-sm text-muted-foreground">
        Welcome
        {user.firstName ? `, ${user.firstName}` : ''}
        {email ? ` (${email})` : ''}
      </p>
      <button
        type="button"
        onClick={handleSignOut}
        className="shrink-0 text-sm font-medium text-foreground underline-offset-4 hover:underline"
      >
        Sign out
      </button>
    </div>
  );
}

function FunnelProgressBar({ step }: { step: number }) {
  return (
    <nav aria-label="Purchase progress">
      {/* Mobile: compact "Step X of 5" line — the five labels don't fit. */}
      <p className="mb-3 text-center text-sm text-muted-foreground sm:hidden">
        Step {Math.min(step, FUNNEL_STEP_LABELS.length)} of {FUNNEL_STEP_LABELS.length}:{' '}
        <span className="font-medium text-foreground">{FUNNEL_STEP_LABELS[step - 1]}</span>
      </p>

      <ol className="flex items-start justify-between gap-1 sm:gap-2">
        {FUNNEL_STEP_LABELS.map((label, idx) => {
          const stepNumber = idx + 1;
          const isCurrent = stepNumber === step;
          const isDone = stepNumber < step;

          return (
            <li
              key={label}
              className="flex min-w-0 flex-1 flex-col items-center"
              aria-current={isCurrent ? 'step' : undefined}
            >
              <div className="flex w-full items-center">
                <div
                  className={cn(
                    'h-0.5 flex-1',
                    idx === 0 ? 'bg-transparent' : isDone || isCurrent ? 'bg-primary' : 'bg-border'
                  )}
                />
                <div
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-medium transition-colors',
                    isDone && 'border-primary bg-primary text-primary-foreground',
                    isCurrent && 'border-primary bg-background text-primary',
                    !isDone && !isCurrent && 'border-border bg-background text-muted-foreground'
                  )}
                >
                  {isDone ? <Check className="h-4 w-4" /> : stepNumber}
                </div>
                <div
                  className={cn(
                    'h-0.5 flex-1',
                    idx === FUNNEL_STEP_LABELS.length - 1
                      ? 'bg-transparent'
                      : isDone
                        ? 'bg-primary'
                        : 'bg-border'
                  )}
                />
              </div>
              <span
                className={cn(
                  'mt-2 hidden px-1 text-center text-xs leading-tight sm:block',
                  isCurrent ? 'font-semibold text-foreground' : 'text-muted-foreground'
                )}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
